import React, { useState, useEffect, useMemo } from 'react';
import {
  ChefHat,
  Sandwich,
  Egg, 
  Soup, 
  UtensilsCrossed,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { getRecipeSuggestions } from '../../../utils/recipeMatching';
import RecipeCard from './RecipeCard';

/**
 * Effort levels for meal suggestions
 */
const EFFORT_LEVELS = [
  { 
    id: 'no-cook', 
    icon: Sandwich,
    name: 'No cook', 
    subtitle: 'Just combine, no cleanup'
  },
  { 
    id: 'one-pan', 
    icon: Egg,
    name: 'One pan', 
    subtitle: 'Quick prep, one pan'
  },
  { 
    id: 'some-steps', 
    icon: Soup,
    name: 'Some steps', 
    subtitle: 'Chopping + cooking'
  },
  { 
    id: 'full-cook', 
    icon: UtensilsCrossed,
    name: 'Full cook', 
    subtitle: 'Full spread - main + sides'
  }
];

/**
 * Category priority for ingredient display
 */
const CATEGORY_PRIORITY = {
  protein: 1, veg: 2, fruit: 3, dairy: 4, 
  carb: 5, other: 6, beverage: 7, condiment: 8
};

/**
 * Get default effort level based on mood
 */
function getDefaultEffort(mood) {
  switch (mood) {
    case 'Rough':
    case 'Meh':
      return 'no-cook';
    case 'Okay':
      return 'one-pan';
    case 'Good':
      return 'some-steps';
    case 'Great':
      return 'full-cook';
    default:
      return 'one-pan';
  }
}

/**
 * MealsTab - Meal suggestions based on pantry and energy
 */
export default function MealsTab({
  stockedItems = [],
  currentState = {},
  onOpenChatWithMessage
}) {
  const [effortLevel, setEffortLevel] = useState(() => getDefaultEffort(currentState.mood));
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [userChangedEffort, setUserChangedEffort] = useState(false);

  useEffect(() => {
    if (!userChangedEffort && currentState.mood) {
      setEffortLevel(getDefaultEffort(currentState.mood));
    }
  }, [currentState.mood, userChangedEffort]);

  // Sort ingredients by category priority
  const sortedIngredients = useMemo(() => {
    return [...stockedItems].sort((a, b) => {
      const priorityA = CATEGORY_PRIORITY[a.category] || 6;
      const priorityB = CATEGORY_PRIORITY[b.category] || 6;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.name.localeCompare(b.name);
    });
  }, [stockedItems]);

  // Get recipe matches - instant, no API call
  const recipeMatches = useMemo(() => {
    // If specific ingredients selected, filter pantry to those
    const itemsToMatch = selectedIngredients.length > 0
      ? stockedItems.filter(item => selectedIngredients.includes(item.id))
      : stockedItems;
    
    return getRecipeSuggestions(itemsToMatch, effortLevel);
  }, [stockedItems, effortLevel, selectedIngredients]);

  const INITIAL_SHOW_COUNT = 12;
  const visibleIngredients = showAllIngredients 
    ? sortedIngredients 
    : sortedIngredients.slice(0, INITIAL_SHOW_COUNT);
  const hiddenCount = sortedIngredients.length - INITIAL_SHOW_COUNT;

  const handleEffortChange = (level) => {
    setEffortLevel(level);
    setUserChangedEffort(true);
  };

  const toggleIngredient = (itemId) => {
    setSelectedIngredients(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const buildMabelMessage = () => {
    const effort = EFFORT_LEVELS.find(e => e.id === effortLevel);
    const selectedItems = stockedItems.filter(item => selectedIngredients.includes(item.id));
    
    if (selectedItems.length > 0) {
      const ingredientNames = selectedItems.map(i => i.name).join(', ');
      return `What can I make with ${ingredientNames}? I can manage ${effort.name.toLowerCase()} cooking.`;
    }
    
    return `What can I make? I can manage ${effort.name.toLowerCase()} cooking.`;
  };

  const handleAskMabel = () => {
    const message = buildMabelMessage();
    onOpenChatWithMessage(message);
  };

  const totalMatches = recipeMatches.canMake.length + recipeMatches.almostCanMake.length;

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* Effort Level Selector */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: ABLE_COLORS.textPrimary,
          marginBottom: '12px'
        }}>
          What can you manage right now?
        </label>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px'
        }}>
          {EFFORT_LEVELS.map((level) => {
            const isSelected = effortLevel === level.id;
            const IconComponent = level.icon;
            return (
              <button
                key={level.id}
                onClick={() => handleEffortChange(level.id)}
                style={{
                  padding: '12px',
                  background: isSelected ? `${ABLE_COLORS.primary}10` : ABLE_COLORS.bgSubtle,
                  border: `2px solid ${isSelected ? ABLE_COLORS.primary : 'transparent'}`,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <IconComponent 
                    size={20} 
                    color={isSelected ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary}
                  />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary
                  }}>
                    {level.name}
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: ABLE_COLORS.textMuted,
                  paddingLeft: '28px'
                }}>
                  {level.subtitle}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Ingredient Filter */}
      {stockedItems.length > 0 && (
        <div style={{
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          padding: '16px',
          marginBottom: '16px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary,
            marginBottom: '8px'
          }}>
            Use specific ingredients?
            <span style={{
              fontWeight: '400',
              color: ABLE_COLORS.textMuted,
              marginLeft: '8px'
            }}>
              (optional)
            </span>
          </label>
          <p style={{
            fontSize: '12px',
            color: ABLE_COLORS.textMuted,
            margin: '0 0 12px 0'
          }}>
            Tap to select ingredients you want to use
          </p>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {visibleIngredients.map((item) => {
              const isSelected = selectedIngredients.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => toggleIngredient(item.id)}
                  style={{
                    padding: '6px 12px',
                    background: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
                    color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                    border: 'none',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {item.name}
                </button>
              );
            })}
            
            {hiddenCount > 0 && (
              <button
                onClick={() => setShowAllIngredients(!showAllIngredients)}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: ABLE_COLORS.primary,
                  border: `1px dashed ${ABLE_COLORS.primary}`,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {showAllIngredients ? (
                  <>Show less <ChevronUp size={14} /></>
                ) : (
                  <>+{hiddenCount} more <ChevronDown size={14} /></>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Recipe Results */}
      {stockedItems.length > 0 && totalMatches > 0 && (
        <div style={{ marginBottom: '16px', width: '100%' }}>
          {/* Can Make */}
          {recipeMatches.canMake.length > 0 && (
            <>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.textPrimary,
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Ready to make
                <span style={{
                  background: ABLE_COLORS.success,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}>
                  {recipeMatches.canMake.length}
                </span>
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {recipeMatches.canMake.map(recipe => (
                  <RecipeCard key={recipe.id} recipe={recipe} />
                ))}
              </div>
            </>
          )}

          {/* Almost Can Make */}
          {recipeMatches.almostCanMake.length > 0 && (
            <>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.textPrimary,
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Missing 1-2 ingredients
                <span style={{
                  background: ABLE_COLORS.coral,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}>
                  {recipeMatches.almostCanMake.length}
                </span>
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {recipeMatches.almostCanMake.map(recipe => (
                  <RecipeCard key={recipe.id} recipe={recipe} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* No matches state */}
      {stockedItems.length > 0 && totalMatches === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '24px',
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          marginBottom: '16px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <p style={{
            fontSize: '14px',
            color: ABLE_COLORS.textMuted,
            margin: 0
          }}>
            No recipes match your current pantry for this effort level. Try a different level or ask Mabel for custom ideas!
          </p>
        </div>
      )}

      {/* Ask Mabel CTA - positioned as premium upgrade */}
      <button
        onClick={handleAskMabel}
        style={{
          width: '100%',
          padding: '14px',
          background: 'white',
          border: `2px solid ${ABLE_COLORS.primary}`,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          color: ABLE_COLORS.primary,
          fontWeight: '600',
          fontSize: '15px',
          marginBottom: '16px'
        }}
      >
        <Sparkles size={18} />
        Want more ideas? Ask Mabel
      </button>

      {/* Empty state */}
      {stockedItems.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '32px 20px',
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <ChefHat size={40} color={ABLE_COLORS.textMuted} style={{ marginBottom: '12px' }} />
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary,
            margin: '0 0 8px 0'
          }}>
            Add ingredients first
          </h3>
          <p style={{
            fontSize: '14px',
            color: ABLE_COLORS.textMuted,
            margin: 0
          }}>
            Go to Pantry to add what you have, then come back for ideas
          </p>
        </div>
      )}
    </div>
  );
}
